import React from 'react';
import {
  Container,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import styled from 'styled-components';
import { useLanguage } from '../../contexts/LanguageContext';

const Section = styled(Box)`
  padding: 4rem 0;
`;

const StyledAccordion = styled(Accordion)`
  margin-bottom: 1rem !important;
  border-radius: 8px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;

  &:before {
    display: none;
  }
`;

const StyledAccordionSummary = styled(AccordionSummary)`
  .MuiAccordionSummary-content {
    margin: 1rem 0;
  }
`;

const VideoContainer = styled(Box)`
  position: relative;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  height: 0;
  overflow: hidden;
  margin: 1rem 0;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

const ReferenceLink = styled(Link)`
  display: block;
  margin-top: 1rem;
  color: ${({ theme }) => theme.palette.primary.main};
  text-decoration: none;
  font-size: 0.9rem;

  &:hover {
    text-decoration: underline;
  }
`;

const FAQ: React.FC = () => {
  const { t } = useLanguage();

  return (
    <Container maxWidth="lg">
      <Section>
        <Typography variant="h3" gutterBottom align="center">
          {t('faq.title')}
        </Typography>
        <Box mt={4}>
          <StyledAccordion>
            <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                {t('faq.security.question')}
              </Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <Typography>
                {t('faq.security.answer')}
              </Typography>
              <ReferenceLink 
                href="https://docs.chia.net/bls-keys/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('faq.security.learnMore')}
              </ReferenceLink>
            </AccordionDetails>
          </StyledAccordion>

          <StyledAccordion>
            <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                {t('faq.monitoring.question')}
              </Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <Typography>
                {t('faq.monitoring.answer')}
              </Typography>
            </AccordionDetails>
          </StyledAccordion>

          <StyledAccordion>
            <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                {t('faq.masterKey.question')}
              </Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <Typography gutterBottom>
                {t('faq.masterKey.answer')}
              </Typography>
              <VideoContainer>
                <iframe
                  src="https://www.youtube.com/embed/your_video_id"
                  title={t('faq.masterKey.videoTitle')}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </VideoContainer>
              <ReferenceLink 
                href="https://docs.chia.net/troubleshooting/locating-keys/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('faq.masterKey.learnMore')}
              </ReferenceLink>
            </AccordionDetails>
          </StyledAccordion>

          <StyledAccordion>
            <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                {t('faq.addresses.question')}
              </Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <Typography>
                {t('faq.addresses.answer')}
              </Typography>
            </AccordionDetails>
          </StyledAccordion>

          <StyledAccordion>
            <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                {t('faq.singleAddress.question')}
              </Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <Typography>
                {t('faq.singleAddress.answer')}
              </Typography>
            </AccordionDetails>
          </StyledAccordion>

          <StyledAccordion>
            <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                {t('faq.notifications.question')}
              </Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <Typography>
                {t('faq.notifications.answer')}
              </Typography>
            </AccordionDetails>
          </StyledAccordion>

          <StyledAccordion>
            <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                {t('faq.vault.question')}
              </Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <Typography>
                {t('faq.vault.answer')}
              </Typography>
              <ReferenceLink 
                href="https://www.chia.net/2025/01/31/chia-vaults-a-secure-and-flexible-way-to-manage-your-digital-assets/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('faq.vault.learnMore')}
              </ReferenceLink>
            </AccordionDetails>
          </StyledAccordion>

          <StyledAccordion>
            <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                {t('faq.clawback.question')}
              </Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <Typography>
                {t('faq.clawback.answer')}
              </Typography>
            </AccordionDetails>
          </StyledAccordion>

          <StyledAccordion>
            <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                {t('faq.vaultSupport.question')}
              </Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <Typography>
                {t('faq.vaultSupport.answer')}
              </Typography>
            </AccordionDetails>
          </StyledAccordion>

          <StyledAccordion>
            <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                {t('faq.tokens.question')}
              </Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <Typography>
                {t('faq.tokens.answer')}
              </Typography>
            </AccordionDetails>
          </StyledAccordion>

          <StyledAccordion>
            <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                {t('faq.nft.question')}
              </Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <Typography>
                {t('faq.nft.answer')}
              </Typography>
            </AccordionDetails>
          </StyledAccordion>

          <StyledAccordion>
            <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                {t('faq.subscription.question')}
              </Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <Typography>
                {t('faq.subscription.answer')}
              </Typography>
            </AccordionDetails>
          </StyledAccordion>
        </Box>
      </Section>
    </Container>
  );
};

export default FAQ; 